import React, {FC, memo, ReactNode, useEffect, useRef, useState} from "react";
import {Alert, Button, Checkbox, Collapse, Modal, Space, Spin, Steps, Tooltip, message} from "antd";
import {CheckCircleFilled, InfoCircleTwoTone, WarningFilled} from "@ant-design/icons";
import {green, orange} from "@ant-design/colors";
import {useHistory} from "react-router-dom";
import {useApolloClient} from "@apollo/client";
import {
    BookingType,
    ExecuteBulkTransferMutation,
    GetBookedAttendeesByCourseIdDocument,
    GetBookingsByCourseDocument,
    GetCourseByIdDocument,
    TransferBulkPlanInput,
    TransferOldBookingOption,
    TransferOldBookingTreatment,
    TransferPriceKind,
    useExecuteBulkTransferMutation,
    useTransferBulkPlanPreviewLazyQuery,
    useVoidMolliePaymentLinksForBookingsMutation,
} from "../../../generated/graphql";
import {TableType} from "../../courses/CourseEdit/CourseListColumns";
import {CurrencyShort, renderAmount} from "../../../helpers/renderAmount";
import formatDate from "../../../helpers/formatDate";
import {URL_COURSES_DETAILS_ID} from "../../../models/url";
import i18n from "../../../services/i18n";
import useTransferWizardMode from "../../../hooks/useTransferWizardMode";
import TransferOptionList, {TransferOption} from "../../bookings/TransferWizard/TransferOptionList";
import {Blockers, canApplyCutoff, defaultOldBookingOption, OLD_BOOKING_CUTOFF, proRataSuggested, OutcomeUnknownAlert, outcomeUnknownDetail, TargetStartedHint} from "../../bookings/TransferWizard/TransferWizardSteps";
import {TRANSFER_WIZARD_WIDTH_COMPACT, TRANSFER_WIZARD_WIDTH_FULL, transferWizardModalStyle} from "../../bookings/TransferWizard/transferWizardLayout";
import BulkTransferPreviewTable, {blockedRows, BulkRow, invoiceNowFor} from "./BulkTransferPreviewTable";
import BulkDunningAlert from "../../invoice/SettlementDetails/BookingDunnings/BulkDunningAlert";

/**
 * Bulk-Umbuchung über die Engine (#666, ADR 0006) — der Wizard des Wizard-Modus im Dialog
 * „Verschieben oder Kopieren“ (TableToolBar). Der Zielkurs kommt aus dem umgebenden Dialog.
 *
 * Zwei Schritte (Entwurf docs/sketches/transfer-bulk): Entscheidungen (Preis, Abrechnung,
 * E-Mail, Notizen, besuchte Lektionen) → Vorschau als Tabelle je Buchung. Die Entscheidungen
 * gelten für alle Buchungen gleich; was mit den alten Buchungen passiert, folgt festen Regeln
 * (Löschen, Storno ohne/mit Beleg) und steht nur in der Vorschau. Der Überschuss bleibt
 * Guthaben. Geschrieben wird nur in der Vorschau über „Umbuchungen jetzt ausführen“ — alle
 * Buchungen oder keine.
 */

export type BulkTransferWizardProps = {
    open: boolean;
    currentCourseId: string;
    targetCourse: TableType;
    bookingIds: Array<string>;
    onClose: () => void;
    /** Nach erfolgreicher Ausführung (der umgebende Dialog schließt und lädt neu). */
    onFinished: () => void;
};

type Preview = NonNullable<ReturnType<typeof useTransferBulkPlanPreviewLazyQuery>[1]["data"]>["transferBulkPlanPreview"];
type ExecuteResult = ExecuteBulkTransferMutation["executeBulkTransfer"];
type InvoiceChoice = "rule" | "always" | "never";

const STEP_DECISIONS = 0;
const STEP_PREVIEW = 1;
const GREY = "rgba(0,0,0,0.65)";

const money = (amount: number, currency?: CurrencyShort): string => renderAmount({amount: Math.round(amount * 100) / 100, currency});
const invoiceLabel = (ref: {invoiceNumberPrefix: string; invoiceNumber: number}): string => `${ref.invoiceNumberPrefix}-${ref.invoiceNumber}`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const extractGraphQlErrorCode = (error: any): string | undefined => (error?.graphQLErrors?.[0]?.extensions ? error.graphQLErrors[0].extensions.code : undefined);

/** „{amount} je Buchung“, bei unterschiedlichen Beträgen (Mitgliederrabatt je Bucher) „ab {amount} je Buchung“. */
const perBookingText = (amounts: Array<number>, currency?: CurrencyShort): string => {
    const tr = i18n.containers.bookings.TransferWizard.bulk.price;
    const min = Math.min(...amounts);
    const allSame = amounts.every((amount) => Math.abs(amount - min) < 0.005);

    return allSame ? tr.perBooking({amount: money(min, currency)}) : tr.perBookingFrom({amount: money(min, currency)});
};

const BulkTransferWizard: FC<BulkTransferWizardProps> = ({open, currentCourseId, targetCourse, bookingIds, onClose, onFinished}) => {
    const tr = i18n.containers.bookings.TransferWizard;
    const trBulk = tr.bulk;
    const {currency, defaults} = useTransferWizardMode();
    const history = useHistory();

    const [step, setStep] = useState(STEP_DECISIONS);
    const [priceOption, setPriceOption] = useState<TransferPriceKind>(TransferPriceKind.CoursePrice);
    const [cutoffRule, setCutoffRule] = useState(false);
    const [deleteNotes, setDeleteNotes] = useState(false);
    const [sendMail, setSendMail] = useState<boolean | null>(null);
    /** null = Regel des Servers je Buchung (sofort, wenn A abgerechnet war). */
    const [invoiceOverride, setInvoiceOverride] = useState<boolean | null>(null);
    /** Vorschau mit Kurspreis: Grundlage für Preise je Option, besuchte Lektionen, frühe Blocker. */
    const [basePreview, setBasePreview] = useState<Preview | undefined>(undefined);
    /** Vorschau mit allen Entscheidungen; ihre Token werden ausgeführt. */
    const [preview, setPreview] = useState<Preview | undefined>(undefined);
    const [errorText, setErrorText] = useState<string | undefined>(undefined);
    const [busy, setBusy] = useState(false);
    /** Nur die Ausführung (nicht das Laden einer Vorschau): Vorschau bleibt stehen, Schließen gesperrt. */
    const [executing, setExecuting] = useState(false);
    /** Technische Meldung, wenn die Ausführung ohne Antwort endete (`outcomeUnknownDetail`) — sperrt jede weitere Ausführung. */
    const [outcomeUnknown, setOutcomeUnknown] = useState<string | undefined>(undefined);
    const busyRef = useRef(false);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Wie im Einzel-Wizard: kein Platz für ALLE → alle auf die Warteliste (eine Entscheidung
    // für den ganzen Lauf, kein gemischter Lauf). Die Server-Vorschau bleibt maßgeblich.
    const waitlist = (targetCourse.freePlaces ?? 0) < bookingIds.length;
    const targetBookingType = waitlist ? BookingType.Waitlist : BookingType.Booking;

    const [loadBulkPreview] = useTransferBulkPlanPreviewLazyQuery({fetchPolicy: "network-only"});
    // Refetch-Regel (Apollo 3.14, Prüfung 24.09.2026): Namen laden nur Abfragen neu, die beim
    // EINTREFFEN der Antwort gemountet sind — ohne Treffer kosten sie nichts und decken die Seite,
    // auf die während der Ausführung navigiert wurde. Dokument-Einträge laufen IMMER und lohnen nur
    // für Cache-Einträge, die später cache-first gelesen werden (Quell-/Zielkurs). Entfernt, weil
    // ihr Ergebnis nie gelesen wird (die Listen laden network-only, die Kursseite no-cache):
    // GetBookins, Buchungsübersicht, Abrechnungsliste, Warteliste von A, Kursdetails von A und B.
    // „GetBookins“ ganz entfernt: keine Komponente führt die Abfrage aus.
    const refetchQueries = [
        "GetBookingsByCourse",
        {query: GetBookingsByCourseDocument, variables: {id: currentCourseId}},
        {query: GetBookingsByCourseDocument, variables: {id: targetCourse.id}},
        "getCourseById",
        {query: GetCourseByIdDocument, variables: {id: currentCourseId}},
        {query: GetCourseByIdDocument, variables: {id: targetCourse.id}},
        "GetBookingsOverviewOptimized",
        "GetUnsettledBookings",
        "GetSettledBookings",
        "GetBookedAttendeesByCourseId",
        {query: GetBookedAttendeesByCourseIdDocument, variables: {courseId: currentCourseId}},
        {query: GetBookedAttendeesByCourseIdDocument, variables: {courseId: targetCourse.id}},
        "GetWaitlistByCourse",
        "GetCourseDetailsById",
        "GetCoursesWithPagination",
        "getFreePlacesByCourseId",
        "GetBookerDetails",
        "GetCreditEntriesByBookerId",
    ];
    const [executeBulkTransfer] = useExecuteBulkTransferMutation({refetchQueries});
    // Ohne Refetch: der Wizard lädt danach seine eigene Vorschau neu; ein Refetch der Buchungslisten baut
    // auf der Kursseite den umgebenden Dialog samt Wizard ab. Die Listen lädt `close` nach, eine
    // Ausführung über `executeBulkTransfer`.
    const [voidPaymentLinks] = useVoidMolliePaymentLinksForBookingsMutation();
    const apolloClient = useApolloClient();
    const paymentsVoidedRef = useRef(false);

    /**
     * X, Maske und ESC: während der Ausführung gesperrt. ESC bleibt bewusst aktiv (`keyboard`):
     * rc-dialog stoppt die Taste nur dann, sonst wandert sie zum Eltern-Dialog „Verschieben oder
     * Kopieren“ und schließt ihn samt Wizard (Review #1020).
     */
    const closeUnlessExecuting = () => {
        if (!executing) close();
    };

    const close = () => {
        if (paymentsVoidedRef.current || outcomeUnknown !== undefined) {
            apolloClient.refetchQueries({include: refetchQueries.map((entry) => (typeof entry === "string" ? entry : entry.query))}).catch(() => undefined);
        }
        onClose();
    };

    /** `transferDate` der ersten Vorschau geht in jede weitere und in die Ausführung (Drift-Guard, #866). */
    const buildPlanInput = (kind: TransferPriceKind, withDecisions: boolean): TransferBulkPlanInput => ({
        sourceBookingIds: bookingIds,
        targetCourseId: targetCourse.id,
        targetBookingType,
        priceOption: {kind, transferDate: basePreview?.rows[0]?.plan.transferDate},
        // Vorgabe für alle; der Server wendet die Stichtag-Regel je Buchung nur an, wo sie greift.
        ...(withDecisions && cutoffRule ? {oldBookingOption: TransferOldBookingOption.CutoffRule} : {}),
    });

    /** Vorschau mit Kurspreis ohne Entscheidungen — Grundlage des Entscheidungs-Schritts. */
    const baseInput: TransferBulkPlanInput = {sourceBookingIds: bookingIds, targetCourseId: targetCourse.id, targetBookingType, priceOption: {kind: TransferPriceKind.CoursePrice}};

    const loadPreview = async (input: TransferBulkPlanInput): Promise<Preview> => {
        const result = await loadBulkPreview({variables: {input}});

        if (result.error) throw result.error;
        const loaded = result.data?.transferBulkPlanPreview;

        if (!loaded) throw new Error(trBulk.errors.previewFailed());

        return loaded;
    };

    const runGuarded = async (action: () => Promise<void>): Promise<void> => {
        if (busyRef.current) return;
        busyRef.current = true;
        setBusy(true);
        setErrorText(undefined);
        try {
            await action();
        } catch (error) {
            if (!isMountedRef.current) return;
            if (extractGraphQlErrorCode(error) === "MOLLIE_ACTIVE_PAYMENT_LINK") {
                const trLink = i18n.containers.bookings.TransferMolliePaymentLink.bulk;

                Modal.warning({title: trLink.knownErrorTitle(), content: trLink.knownErrorMessage()});
            }
            setErrorText(error instanceof Error ? error.message : String(error));
        } finally {
            busyRef.current = false;
            if (isMountedRef.current) setBusy(false);
        }
    };

    // Beim Öffnen: Vorschau mit Kurspreis, Entscheidungen auf die Tenant-Standardwerte.
    // `TransferMultipleBookings` rendert den Wizard bedingt (Mount je Öffnen) — die Rücksetzung
    // greift damit auch, wenn `open` als Literal nie kippt.
    useEffect(() => {
        if (!open) return;
        setStep(STEP_DECISIONS);
        setBasePreview(undefined);
        setPreview(undefined);
        setDeleteNotes(false);
        setSendMail(null);
        setInvoiceOverride(null);
        runGuarded(async () => {
            const base = await loadPreview(baseInput);

            if (!isMountedRef.current) return;
            setBasePreview(base);
            // Alle Zeilen haben denselben Zielkurs: läuft er schon und hat noch Lektionen, ist „anteilig" Vorschlag (#1557, wie im Einzel-Wizard).
            setPriceOption(proRataSuggested(base.rows[0]?.plan.priceOptions ?? []) ? TransferPriceKind.ProRataRemainingLessons : TransferPriceKind.CoursePrice);
            setCutoffRule(base.rows.some((row) => defaultOldBookingOption(row.plan, defaults?.oldBookingOption) === OLD_BOOKING_CUTOFF));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const rows = basePreview?.rows ?? [];
    const templateAvailable = basePreview?.templateAvailable ?? false;
    const effectiveSendMail = templateAvailable && (sendMail ?? true);
    const cutoffCount = rows.filter((row) => canApplyCutoff(row.plan)).length;
    /** Regel des Servers je Zeile: sofort abrechnen, wenn A abgerechnet war (nie für die Warteliste). */
    const settledCount = rows.filter((row) => invoiceNowFor(row, null, waitlist)).length;
    const mixed = settledCount > 0 && settledCount < rows.length;
    const count = String(rows.length);

    const blocked = rows.filter((row) => row.plan.blockers.length > 0);
    const linkRows = blocked.filter((row) => row.plan.hasActivePaymentLink);

    /**
     * Blocker „aktiver Zahlungslink": nach Rückfrage die Links der betroffenen Buchungen entwerten
     * und die Vorschau neu laden — die Entscheidungen bleiben. Der Server entwertet je Buchung
     * (Best-Effort); was nicht klappt, steht danach als Fehler im Schritt und bleibt Blocker.
     */
    const voidLinks = () => {
        const trVoid = trBulk.voidLinks;
        const bookings = linkRows.map((row) => row.sourceBookingNumber).join(", ");
        const linkCount = String(linkRows.length);

        Modal.confirm({
            width: 600,
            icon: null,
            title: trVoid.confirmTitle({count: linkCount}),
            content: trVoid.confirmContent({bookings}),
            okText: trVoid.button({count: linkCount, bookings}),
            okButtonProps: {danger: true},
            onOk: async () => runGuarded(async () => {
                const hideLoading = message.loading(trVoid.voiding(), 0);
                let results: Array<{bookingId: string; error?: string | null}> = [];

                try {
                    // Vor dem Aufruf: auch ein fehlgeschlagener Versuch kann Links schon entwertet haben (#1034).
                    paymentsVoidedRef.current = true;
                    const voided = await voidPaymentLinks({variables: {bookingIds: linkRows.map((row) => row.sourceBookingId)}});

                    results = voided.data?.voidMolliePaymentLinksForBookings ?? [];
                } finally {
                    hideLoading();
                }
                const base = await loadPreview(baseInput);

                if (!isMountedRef.current) return;
                setBasePreview(base);
                // Maßgeblich ist die neue Vorschau: wo dort noch ein Link offen ist, hat das Entwerten nicht gegriffen.
                const stillOpen = base.rows.filter((row) => row.plan.hasActivePaymentLink);

                if (stillOpen.length > 0) {
                    const reason = (bookingId: string): string => {
                        const error = results.find((result) => result.bookingId === bookingId)?.error;

                        return error ? ` (${error})` : "";
                    };

                    throw new Error(trVoid.failed({bookings: stillOpen.map((row) => `${row.sourceBookingNumber}${reason(row.sourceBookingId)}`).join(", ")}));
                }
                message.success(trVoid.done());
            }),
        });
    };

    const reloadPreviewAndGo = () => runGuarded(async () => {
        setStep(STEP_PREVIEW);
        const next = await loadPreview(buildPlanInput(priceOption, true));

        if (!isMountedRef.current) return;
        setPreview(next);
    });

    const execute = () => runGuarded(async () => {
        if (!preview || preview.blockers.length > 0 || blockedRows(preview.rows) > 0) return;
        setExecuting(true);
        try {
            await runExecution(preview);
        } catch (error) {
            const detail = outcomeUnknownDetail(error);

            if (detail === undefined) throw error;
            if (isMountedRef.current) setOutcomeUnknown(detail);
        } finally {
            if (isMountedRef.current) setExecuting(false);
        }
    });

    /** Ausführung mit den Token der bestätigten Vorschau `ready`. */
    const runExecution = async (ready: Preview) => {
        const executionResult = await executeBulkTransfer({
            variables: {
                input: {
                    plan: buildPlanInput(priceOption, true),
                    expected: ready.rows.map((row) => ({
                        sourceBookingId: row.sourceBookingId,
                        confirmationToken: row.plan.confirmationToken,
                        expectedPriceB: row.plan.priceB,
                        expectedDiscountB: row.plan.discountB,
                        expectedAppliedToB: row.plan.appliedToB,
                        expectedSurplus: row.plan.surplus,
                        expectedOldBookingTreatment: row.plan.oldBooking.treatment,
                        expectedCarriedOverItems: row.plan.carriedOverItems.map((item) => ({type: item.type, amount: item.amount, title: item.title ?? null})),
                    })),
                    deleteNotes,
                    // Ohne ausdrückliche Wahl gilt je Buchung die Server-Regel. Mit Wahl folgt der
                    // Rechnungsanhang ihr: sonst behielte eine Zeile den Anhang-Default ihrer alten Buchung.
                    communication: {sendMail: effectiveSendMail, ...(invoiceOverride !== null ? {createInvoiceBNow: invoiceOverride, attachInvoiceB: invoiceOverride} : {})},
                },
            },
        });
        const executed = executionResult.data?.executeBulkTransfer;

        if (!executed) throw new Error(tr.errors.executionFailed());
        message.success(`${trBulk.result.title({count: String(executed.results.length)})}`);
        showResult(executed, ready.rows as Array<BulkRow>);
        if (isMountedRef.current) onFinished();
    };

    // ── Ergebnis ──────────────────────────────────────────────────────────────────────
    /** Ergebnis als eigenständiger Dialog: die Refetches bauen den Wizard samt Elterndialog ab (wie im Einzel-Wizard). */
    const showResult = (executed: ExecuteResult, previewRows: Array<BulkRow>) => {
        const trResult = trBulk.result;
        const attendeeOf = (sourceBookingId: string): string => previewRows.find((row) => row.sourceBookingId === sourceBookingId)?.attendeeName ?? "";
        const reversals = executed.results.map((row) => row.reversalInvoice).filter((ref): ref is NonNullable<typeof ref> => Boolean(ref));
        const invoices = executed.results.map((row) => row.invoiceB).filter((ref): ref is NonNullable<typeof ref> => Boolean(ref));
        const mails = executed.results.filter((row) => row.mailSent).length;
        const invoicesWanted = !waitlist && (invoiceOverride ?? settledCount > 0);
        const done: Array<{key: string; text: ReactNode; warn?: boolean}> = [];

        if (reversals.length > 0) {
            const numbers = reversals.map(invoiceLabel).join(", ");

            done.push({key: "reversals", text: reversals.length === 1 ? trResult.reversalCreated({numbers}) : trResult.reversalsCreated({count: String(reversals.length), numbers})});
        }
        if (invoices.length > 0) {
            const numbers = invoices.map(invoiceLabel).join(", ");

            done.push({key: "invoices", text: invoices.length === 1 ? trResult.invoiceCreated({numbers}) : trResult.invoicesCreated({count: String(invoices.length), numbers})});
        } else if (invoicesWanted) {
            // Sofort abrechnen war gewollt, aber keine Nacharbeit hat eine Rechnung erzeugt — die Warnzeilen nennen die Gründe.
            done.push({key: "invoices-failed", text: trResult.invoicesFailed(), warn: true});
        } else if (!waitlist) {
            done.push({key: "invoices-later", text: trResult.invoicesLater()});
        }
        if (mails > 0) {
            done.push({key: "mails", text: mails === 1 ? trResult.mailSentOne() : trResult.mailSent({count: String(mails)})});
        } else if (!effectiveSendMail) {
            done.push({key: "mails-none", text: trResult.mailNone()});
        }
        // Nacharbeiten, die nach dem Commit fehlgeschlagen sind — Warnzeilen, nicht Fehler: die Umbuchungen sind verbucht.
        executed.warnings.forEach((warning, index) => done.push({key: `warning-${index}`, text: warning, warn: true}));

        const oldBookingText = (row: ExecuteResult["results"][number]): string => {
            if (row.oldBookingDeleted || row.plan.oldBooking.treatment === TransferOldBookingTreatment.Delete) return trResult.oldDeleted();

            return row.reversalInvoice ? trResult.oldCanceledWithReceipt({number: invoiceLabel(row.reversalInvoice)}) : trResult.oldCanceled();
        };
        /** Offen auf der neuen Buchung = Buchungspreis (Summe der Raten) − Übertrag; nie negativ. */
        const openOf = (row: ExecuteResult["results"][number]): number => Math.max(0, Math.round((row.booking.grossPrice - row.plan.appliedToB) * 100) / 100);
        const goToCourse = () => {
            history.push({pathname: URL_COURSES_DETAILS_ID.replace(":id", targetCourse.id), state: {returnTo: window.location.pathname}});
            Modal.destroyAll();
        };
        const cell = (children: ReactNode, align: "left" | "right" = "left"): ReactNode => <td style={{padding: "6px 8px", borderBottom: "1px solid #f0f0f0", textAlign: align}}>{children}</td>;
        const head = (children: ReactNode, align: "left" | "right" = "left"): ReactNode => <th style={{padding: "6px 8px", borderBottom: "1px solid #f0f0f0", background: "#fafafa", fontWeight: 500, textAlign: align}}>{children}</th>;

        Modal.confirm({
            icon: null,
            width: TRANSFER_WIZARD_WIDTH_COMPACT,
            okText: tr.buttons.done(),
            okButtonProps: {block: true, style: {margin: 0}},
            cancelButtonProps: {style: {display: "none"}},
            maskClosable: true,
            content: <div style={{textAlign: "left"}}>
                <div style={{display: "flex", gap: 14, alignItems: "center", marginBottom: 14}}>
                    <CheckCircleFilled style={{fontSize: 40, color: green[6], flex: "none"}} />
                    <div style={{fontSize: 18, fontWeight: 600, lineHeight: 1.3}}>{trResult.title({count: String(executed.results.length)})}{" "}{waitlist ? trResult.movedToWaitlist() : trResult.movedToCourse()}{" "}<b>{targetCourse.prefixedCourseNumber}</b></div>
                </div>
                {done.length > 0 && <ul style={{listStyle: "none", padding: 0, margin: "0 0 14px"}}>
                    {done.map((entry) => <li key={entry.key} style={{display: "flex", gap: 8, alignItems: "baseline", padding: "2px 0", color: entry.warn ? undefined : GREY}}>
                        {entry.warn ? <WarningFilled style={{color: orange[5]}} /> : <CheckCircleFilled style={{color: green[6]}} />}
                        <span>{entry.text}</span>
                    </li>)}
                </ul>}
                <div style={{maxHeight: "50vh", overflowY: "auto"}}>
                    <table style={{width: "100%", borderCollapse: "collapse", fontSize: 13}}>
                        <thead><tr>{head(trResult.before())}{head("")}{head(trResult.new())}{head(trResult.attendee())}{head(trResult.total(), "right")}{head(trResult.open(), "right")}{head(trResult.oldBooking())}</tr></thead>
                        <tbody>{executed.results.map((row) => <tr key={row.sourceBookingId}>
                            {cell(row.sourceBookingNumber)}{cell(<span style={{color: "rgba(0,0,0,0.45)"}}>→</span>)}
                            {cell(<b>{row.booking.bookingNumberPrefix}{"-"}{row.booking.bookingNumber}</b>)}
                            {cell(attendeeOf(row.sourceBookingId))}{cell(money(row.booking.grossPrice, currency), "right")}{cell(waitlist ? "–" : <b>{money(openOf(row), currency)}</b>, "right")}{cell(oldBookingText(row))}
                        </tr>)}</tbody>
                    </table>
                </div>
                <div style={{marginTop: 12}}><Button type="link" style={{paddingLeft: 0}} onClick={goToCourse}>{trResult.toCourse({course: targetCourse.prefixedCourseNumber ?? ""})}</Button></div>
            </div>,
        });
    };

    // ── Schritte ──────────────────────────────────────────────────────────────────────
    const header = basePreview && (
        <>
        <BulkDunningAlert bookingIds={bookingIds} />
        <Alert
            type={waitlist ? "warning" : "info"}
            showIcon
            style={{marginBottom: 12}}
            message={<span><b>{trBulk.header({count: String(bookingIds.length), course: targetCourse.prefixedCourseNumber ?? ""})}</b>{" — "}{waitlist ? trBulk.asWaitlist({free: String(targetCourse.freePlaces ?? 0), count: String(bookingIds.length)}) : trBulk.asBooking()}</span>}
            description={trBulk.sameForAll()}
        />
        </>
    );

    const limits = (
        <Collapse ghost style={{marginBottom: 12, background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: 6}}>
            <Collapse.Panel key="limits" header={<span><b style={{fontWeight: 500}}>{trBulk.limits.title()}</b>{" — "}<span style={{color: GREY}}>{trBulk.limits.subtitle()}</span></span>}>
                <div style={{color: GREY, fontSize: 13}}>
                    {trBulk.limits.lead()}
                    <ul style={{margin: "4px 0 8px", paddingLeft: 18}}>
                        <li>{trBulk.limits.price()}</li>
                        <li>{trBulk.limits.surplus()}</li>
                        <li>{trBulk.limits.credit()}</li>
                        <li>{trBulk.limits.items()}</li>
                    </ul>
                    {trBulk.limits.single()}
                </div>
            </Collapse.Panel>
        </Collapse>
    );

    const renderPriceOptions = (): Array<TransferOption> => {
        const amountsFor = (kind: TransferPriceKind): Array<number> => rows.map((row) => row.plan.priceOptions.find((option) => option.kind === kind)?.priceB ?? row.plan.priceB);
        const proRata = rows[0]?.plan.priceOptions.find((option) => option.kind === TransferPriceKind.ProRataRemainingLessons);
        const recommended = proRataSuggested(rows[0]?.plan.priceOptions ?? []) ? TransferPriceKind.ProRataRemainingLessons : TransferPriceKind.CoursePrice;
        const courseAmounts = amountsFor(TransferPriceKind.CoursePrice);
        const options: Array<TransferOption> = [{
            key: TransferPriceKind.CoursePrice,
            title: tr.price.coursePrice(),
            recommended: recommended === TransferPriceKind.CoursePrice,
            description: <span>
                <b>{perBookingText(courseAmounts, currency)}</b>{" — "}{trBulk.price.coursePriceDetail({course: targetCourse.prefixedCourseNumber ?? ""})}
                <TargetStartedHint priceOptions={rows[0]?.plan.priceOptions ?? []} course={targetCourse.prefixedCourseNumber ?? ""} />
            </span>,
        }];

        if (proRata) {
            const proRataAmounts = amountsFor(TransferPriceKind.ProRataRemainingLessons);
            const sameAsCourse = proRataAmounts.every((amount, index) => Math.abs(amount - courseAmounts[index]) < 0.005);
            const date = rows[0]?.plan.transferDate ? formatDate(new Date(String(rows[0].plan.transferDate)), "dd.MM.yyyy") : "";

            options.push({
                key: TransferPriceKind.ProRataRemainingLessons,
                title: tr.price.proRata(),
                recommended: recommended === TransferPriceKind.ProRataRemainingLessons,
                description: <span><b>{perBookingText(proRataAmounts, currency)}</b>{" — "}{trBulk.price.proRataDetail({date, remaining: String(proRata.lessonsRemaining ?? 0), total: String(proRata.lessonsTotal ?? 0)})}{sameAsCourse ? ` ${trBulk.price.proRataSameAsCourse()}` : ""}</span>,
            });
        }

        return options;
    };

    /** Abrechnung: bei gemischter Auswahl drei Optionen (Regel mit Zahl), sonst zwei — die Regel fällt mit einer davon zusammen und wird zum Vorschlag. */
    const renderInvoiceOptions = (): {options: Array<TransferOption>; value: InvoiceChoice} => {
        const trInvoice = trBulk.invoice;

        if (mixed) {
            return {
                value: invoiceOverride === null ? "rule" : invoiceOverride ? "always" : "never",
                options: [
                    {key: "rule", title: trInvoice.rule(), recommended: true, description: trInvoice.ruleDescription({settled: String(settledCount), total: count})},
                    {key: "always", title: trInvoice.all(), description: trInvoice.allDescription({count})},
                    {key: "never", title: trInvoice.none(), description: trInvoice.noneDescription({count})},
                ],
            };
        }
        const ruleIsNow = settledCount > 0 && settledCount === rows.length;

        return {
            value: invoiceOverride === null ? (ruleIsNow ? "always" : "never") : invoiceOverride ? "always" : "never",
            options: [
                {key: "always", title: trInvoice.now(), recommended: ruleIsNow, disabled: waitlist, description: <span>{trInvoice.nowDescription()}{ruleIsNow ? <div style={{color: "rgba(0,0,0,0.45)"}}>{trInvoice.nowReason({count})}</div> : null}</span>},
                {key: "never", title: trInvoice.later(), recommended: !ruleIsNow, description: <span>{trInvoice.laterDescription()}{!ruleIsNow && !waitlist ? <div style={{color: "rgba(0,0,0,0.45)"}}>{trInvoice.laterReason()}</div> : null}</span>},
            ],
        };
    };

    const renderDecisions = () => {
        if (!basePreview) return <div style={{minHeight: 220, display: "flex", alignItems: "center", justifyContent: "center"}}><Spin /></div>;
        // Ohne Zeilen (z. B. über dem Limit) gibt es nichts zu entscheiden — nur die Blocker zeigen.
        if (rows.length === 0) return <div>{header}<Blockers blockers={basePreview.blockers} /></div>;
        const invoice = renderInvoiceOptions();

        return <div>
            {header}
            {limits}
            <Blockers blockers={basePreview.blockers} />
            {blocked.length > 0 && <Alert
                type="error"
                showIcon
                style={{marginBottom: 12}}
                message={trBulk.preview.blockedRows({count: String(blocked.length), total: count})}
                description={<ul style={{margin: "4px 0 0", paddingLeft: 18}}>
                    {blocked.map((row) => <li key={row.sourceBookingId}><b>{row.sourceBookingNumber}</b>{row.attendeeName ? ` · ${row.attendeeName}` : ""}{": "}{row.plan.blockers.join(" ")}</li>)}
                </ul>}
                action={linkRows.length > 0 ? <Button size="small" danger disabled={busy} onClick={voidLinks}>{trBulk.voidLinks.button({count: String(linkRows.length), bookings: linkRows.map((row) => row.sourceBookingNumber).join(", ")})}</Button> : undefined}
            />}

            <h4 style={{margin: "0 0 8px", fontWeight: 600}}>{trBulk.price.title()}</h4>
            <TransferOptionList name="bulkPriceOption" layout="cards" options={renderPriceOptions()} value={priceOption} onChange={(key) => setPriceOption(key as TransferPriceKind)} />

            <h4 style={{margin: "18px 0 8px", fontWeight: 600}}>{trBulk.invoice.title()}</h4>
            <TransferOptionList name="bulkInvoice" layout="cards" options={invoice.options} value={invoice.value} onChange={(key) => setInvoiceOverride(key === "rule" ? null : key === "always")} />
            {waitlist && <Alert type="info" showIcon style={{marginTop: 8}} message={tr.communication.waitlistNoInvoice()} />}

            <h4 style={{margin: "18px 0 4px", fontWeight: 600}}>{trBulk.communication.title()}</h4>
            {!templateAvailable && <Alert type="warning" showIcon style={{marginBottom: 8}} message={tr.communication.noTemplate()} />}
            <div><Checkbox name="sendMail" disabled={!templateAvailable} checked={effectiveSendMail} onChange={(event) => setSendMail(event.target.checked)}>{trBulk.communication.sendMail()}</Checkbox></div>
            <div style={{color: "rgba(0,0,0,0.45)", marginLeft: 24, fontSize: 13}}>{trBulk.communication.sendMailDescription()}</div>
            {cutoffCount > 0 && <div style={{marginTop: 8}}>
                <Checkbox name="cutoffRule" checked={cutoffRule} onChange={(event) => setCutoffRule(event.target.checked)}>{tr.oldBooking.cutoffRule()}</Checkbox>
                <div style={{color: "rgba(0,0,0,0.45)", marginLeft: 24, fontSize: 13}}>{trBulk.oldBooking.cutoffRuleDescription({count: String(cutoffCount), total: count})}</div>
            </div>}
            <div style={{marginTop: 8}}>
                <Checkbox name="deleteNotes" checked={deleteNotes} onChange={(event) => setDeleteNotes(event.target.checked)}>
                    {trBulk.oldBooking.deleteNotes()}
                    <Tooltip title={tr.oldBooking.deleteNotesHint()}><InfoCircleTwoTone style={{marginLeft: 5}} /></Tooltip>
                </Checkbox>
            </div>
            <p style={{color: "rgba(0,0,0,0.45)", marginTop: 14, marginBottom: 0, fontSize: 13}}>{trBulk.oldBooking.byRule()}{" "}{tr.communication.afterCommit()}</p>
        </div>;
    };

    const renderPreview = () => {
        // Laden der Vorschau (auch nach „Zurück“ → „Weiter“): Spinner. Bei der Ausführung bleibt die Vorschau stehen.
        if (!preview || (busy && !executing)) {
            return errorText
                ? <Alert type="error" showIcon message={errorText} />
                : <div style={{minHeight: 220, display: "flex", alignItems: "center", justifyContent: "center"}}><Spin tip={trBulk.preview.loading()} /></div>;
        }
        const invoiceNow = preview.rows.filter((row) => invoiceNowFor(row, invoiceOverride, waitlist)).length;

        return <div>
            {header}
            <p>{trBulk.preview.intro()}</p>
            {errorText && <Alert type="error" showIcon closable onClose={() => setErrorText(undefined)} style={{marginBottom: 12}} message={errorText} />}
            {preview.blockers.length > 0 && <Alert type="error" showIcon style={{marginBottom: 12}} message={trBulk.preview.runBlocked()} description={<ul>{preview.blockers.map((blocker, index) => <li key={index}>{blocker}</li>)}</ul>} />}
            {executing && <Alert data-executing type="info" showIcon icon={<Spin size="small" />} style={{marginBottom: 12}} message={trBulk.preview.executing()} />}
            <Spin spinning={executing} indicator={<span />}>
                <BulkTransferPreviewTable rows={preview.rows as Array<BulkRow>} currency={currency} invoiceOverride={invoiceOverride} waitlist={waitlist} />
            </Spin>
            <p style={{marginTop: 12, color: GREY}}>{trBulk.preview.summary({mail: effectiveSendMail ? trBulk.preview.mailYes() : trBulk.preview.mailNo(), now: String(invoiceNow), total: String(preview.rows.length), notes: deleteNotes ? trBulk.preview.notesDropped() : trBulk.preview.notesKept()})}</p>
        </div>;
    };

    const nextDisabled = busy || !basePreview || basePreview.blockers.length > 0 || blocked.length > 0;
    const executeDisabled = busy || outcomeUnknown !== undefined || !preview || preview.blockers.length > 0 || blockedRows(preview.rows) > 0 || Boolean(errorText);
    const footer = (
        <Space>
            <Button danger onClick={close} disabled={executing}>{tr.buttons.cancel()}</Button>
            {step === STEP_PREVIEW && <Button onClick={() => setStep(STEP_DECISIONS)} disabled={busy}>{tr.buttons.back()}</Button>}
            {step === STEP_DECISIONS && <Button type="primary" loading={busy} disabled={nextDisabled} onClick={reloadPreviewAndGo}>{tr.buttons.next()}</Button>}
            {step === STEP_PREVIEW && <Button type="primary" loading={busy} disabled={executeDisabled} onClick={execute}>{trBulk.buttons.execute()}</Button>}
        </Space>
    );

    return (
        <Modal title={<span>{trBulk.title()}{" — "}<span style={{fontWeight: 400}}>{targetCourse.prefixedCourseNumber}</span></span>} open={open} onCancel={closeUnlessExecuting} closable={!executing} maskClosable={!executing} width={step === STEP_PREVIEW ? TRANSFER_WIZARD_WIDTH_FULL : TRANSFER_WIZARD_WIDTH_COMPACT} style={transferWizardModalStyle} footer={footer} destroyOnClose>
            <div>
                <Steps current={step} size="small" style={{marginBottom: 16}}>
                    <Steps.Step title={trBulk.steps.decisions()} />
                    <Steps.Step title={trBulk.steps.preview()} />
                </Steps>
                {outcomeUnknown !== undefined && <OutcomeUnknownAlert detail={outcomeUnknown} />}
                {errorText && step !== STEP_PREVIEW && <Alert type="error" showIcon closable onClose={() => setErrorText(undefined)} style={{marginBottom: 12}} message={errorText} />}
                {step === STEP_DECISIONS ? renderDecisions() : renderPreview()}
            </div>
        </Modal>
    );
};

export default memo(BulkTransferWizard);
